// What a tap on the map selects (spec 15c §3.5): the nearest system or fleet marker within reach;
// a fleet wins when it is closer on screen than the nearest system, so fleets next to or at a
// system (hostile fleets near their target) can still be opened.
import type { MapSystem } from '@stars-and-ships/api-types';
import { type Camera, type Point, worldToScreen } from './camera';
import { type MapFleet, markerPoint } from './fleets';

export type MapSelection = { kind: 'system'; id: string } | { kind: 'fleet'; id: string } | null;

export const PICK_RADIUS_PX = 16;

function nearest<T extends { id: string }>(
  items: readonly T[],
  pointOf: (item: T) => Point,
  camera: Camera,
  at: Point
): { id: string; distance: number } | null {
  let best: { id: string; distance: number } | null = null;
  for (const item of items) {
    const p = worldToScreen(camera, pointOf(item));
    const distance = Math.hypot(p.x - at.x, p.y - at.y);
    if (distance <= PICK_RADIUS_PX && (!best || distance < best.distance)) {
      best = { id: item.id, distance };
    }
  }
  return best;
}

export function pickTap(
  systems: readonly MapSystem[],
  fleets: readonly MapFleet[],
  camera: Camera,
  at: Point,
  now: number
): MapSelection {
  const system = nearest(systems, (s) => s, camera, at);
  const fleet = nearest(fleets, (f) => markerPoint(f, now, camera.scale), camera, at);
  if (fleet && (!system || fleet.distance < system.distance)) {
    return { kind: 'fleet', id: fleet.id };
  }
  return system ? { kind: 'system', id: system.id } : null;
}
